// Tokens — design-system source of truth.
// Values mirror spec/design-system.md §Tokens (lines 12–117); shape per
// spec/tech-arch.md §2.1. Color values are best-guess from the v2.png reference;
// PSD-verification is a separate, later remediation per design-system.md §Tokens.
//
// `as const` is load-bearing: it gives each value its literal type so consumers
// (Tailwind config, primitive renderers) can rely on exact values at compile
// time. Without it, e.g. `tokens.color['ypo-navy']` would widen to `string`.

export const tokens = {
  color: {
    'ypo-navy': '#0d2b53',
    'ypo-navy-deep': '#091f3d',
    'accent-coral': '#e85a3c',
    'accent-gold': '#c5a572',
    'text-primary': '#1f2937',
    'text-secondary': '#6b7280',
    'text-on-dark': '#ffffff',
    // P-037.31 fourth post-Phase-Close §C — page bg #ffffff → #f1f2f5
    // (light gray matching the in-app preview chrome; rendered email's
    // letterbox-side area visually continues with the editor context).
    // **P-048.7 / D-150 — MSO conditional override added in document-
    // shell.ts head.** Banks's Outlook Desktop smoke after P-048.6 §C
    // (`bgcolor=#ffffff` on spacer cells + wrapper inner `<td>`) STILL
    // showed gray bands leaking through inter-Block spacers in Outlook
    // Word renderer — the empty-`&nbsp;` + `line-height: 0` + `font-size:
    // 0` cell is effectively contentless from Outlook's perspective and
    // the renderer skips the bg paint. Fix scope: Outlook ONLY. Modern
    // clients (Apple Mail, iPhone Mail, Gmail web/mobile) render the
    // white-card-over-gray-letterbox design correctly per D-138 fifth
    // post-Phase-Close §A — preserve the design intent there. document-
    // shell.ts head injects `<!--[if mso]><style>body { background-color:
    // #ffffff !important; }</style><![endif]-->` so Outlook desktop
    // sees a white body (transparent spacer cells leak white, matching
    // the wrapper's white). Modern clients ignore the conditional + the
    // body stays #f1f2f5 + the 680 wrapper bgcolor=#ffffff card-over-
    // letterbox renders correctly. P-048.6 §C `bgcolor=#ffffff` on
    // spacer cells + wrapper inner `<td>` RETAINED as belt-and-
    // suspenders (zero harm in any client). Token value unchanged at
    // #f1f2f5.
    'bg-page': '#f1f2f5',
    'bg-section-alt': '#f3f5f8',
    'bg-cta-panel-gold': '#f9f3e6',
    'bg-cta-panel-coral': '#fbeae5',
    rule: '#d8dde3',
    'rule-on-dark': '#1d3a66',
    // P-037.18 D-118 — three promotions from the batched audit (deferred
    // from D-115 once usage decisively crossed the 3-use threshold):
    //
    // - `link-default`: default CTA / inline-link color. P-037.18 introduces
    //   per-Header-style link-color overrides via `RenderContext.linkColor`
    //   (Request 2); this token is the FALLBACK when no Header block is
    //   present. "cta-gold" was rejected as a name — the rendered CTA color
    //   isn't always gold once the Header derivation is in play.
    // - `rule-subtle`: lighter divider/surface than the existing `rule`
    //   (#d8dde3). Used as the per-row-divider rule color in member-list +
    //   day-chairs three-column grids (Banks's "line above each item" pattern)
    //   AND as the footer panel background. The single hex covers both
    //   surface and rule roles cleanly.
    // - `accent-navy-deep`: distinct from the brand `ypo-navy` (#0d2b53) +
    //   `ypo-navy-deep` (#091f3d). Used for the system-wide "≥18 px headlines
    //   render in this color" convention AND the footer's 10 px bottom bar.
    'link-default': '#c69b34',
    'rule-subtle': '#e5e5e5',
    'accent-navy-deep': '#041e42',
  },
  type: {
    // P-048.2 D-145 — dropped quoted `'Helvetica Neue'` from the font
    // stack. Banks's real-send retest (P-048.1 acceptance gate) showed CC's
    // API-ingest preprocessor mangles the quoted-with-space value into an
    // invalid CSS string (`' undefined: HelveticaNeue' undefined: ,...`),
    // causing browsers to fall back to UA-default serif — the root cause of
    // every issue reported on the P-048.1 retest (serif text, missing
    // rules, broken spacing / padding / bold). On macOS / iOS, `Helvetica`
    // is the native sans-serif and is visually near-identical to Helvetica
    // Neue at body text sizes; on Windows the Arial fallback engages; on
    // Linux / web, the platform sans-serif. Banks-confirmed at session
    // opening per OC §11 (LOCKED spec doc amendment to design-system.md
    // with FLR rationale = CC preprocessor incompatibility).
    fontFamily: `Helvetica, Arial, sans-serif`,
    size: { xl: 32, lg: 24, md: 18, base: 16, sm: 14, xs: 12 },
    weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
    leading: { tight: 1.2, normal: 1.5 },
    tracking: { normal: '0', wide: '0.08em' },
  },
  space: { 1: 4, 2: 8, 3: 12, 4: 16, 6: 24, 8: 32, 10: 40, 12: 48, 16: 64 },
  layout: { containerWidth: 680, mobileBreakpoint: 480, cardGap: 16, sectionPaddingY: 40 },
  border: { ruleWidth: 1 },
  radius: { none: 0, sm: 4, md: 8, full: 9999 },
} as const

export type Tokens = typeof tokens
